<template>
	<SectionCard>
		<template #title>
			Cassie Identity Service (CIS)
		</template>
		<template #subtitle>
			Cassie Identity Service allows a Consent Banner to
			re-create and re-add cookies back into the user's
			browser that they have opted into previously but have
			since been deleted from their browser (e.g. by ITP in
			Safari or ETP in Firefox).
		</template>
		<template #title-action>
			<Toggle
				:value="cisEnabled"
				show-status-label
				class="mr-2"
				:disabled="cdcCookieCollectionSelected || !userCanCreateUpdate"
				@update:value="updateBanner('enableCis', $event)"
			/>
		</template>

		<template
			v-if="cisEnabled"
			#body
		>
			<v-row dense>
				<div class="d-flex align-center mr-2">
					{{ cisEnvironment }}
				</div>
				<v-col cols="3">
					<TextField
						v-if="!cdcCookieCollectionSelected"
						:value.sync="cookieBanner.identityServiceEndpoint"
						label="Domain *"
						tooltip-text="Please enter the domain that this Consent Banner will be used on e.g. syrenis.com"
						:rules="{required: true, isLink: true}"
						:disabled="!userCanCreateUpdate"
						@input="updateBanner('identityServiceEndpoint', $event)"
					/>
					<Dropdown
						v-else
						:value.sync="cookieBanner.identityServiceEndpoint"
						:items="mappedAllowedDomains"
						label="Domain *"
						:disabled="!userCanCreateUpdate"
						@input="updateBanner('identityServiceEndpoint', $event)"
					/>
				</v-col>
				<v-col cols="3">
					<PrimaryActionButton
						@click="testConnection"
					>
						Test Connection
					</PrimaryActionButton>
				</v-col>
			</v-row>
			<v-row dense>
				<v-col cols="12">
					Please select which cookies you wish to have protected by
					Cassie Identity Service (CIS)
				</v-col>
			</v-row>
			<v-row dense>
				<v-col cols="4">
					<Dropdown
						v-model="selectedCookieScript"
						:items="cookieScriptItems"
						searchable
						label="Service"
						custom-sort
						:disabled="!userCanCreateUpdate"
						@change="setCookieNameToFirstValue"
					/>
				</v-col>
				<v-col cols="4">
					<Dropdown
						v-model="selectedCookieName"
						:items="cookieNames"
						searchable
						label="Cookie Name"
						:disabled="!userCanCreateUpdate"
					/>
				</v-col>
				<v-col cols="4">
					<PrimaryActionButton
						:disabled="isAddButtonDisabled || !userCanCreateUpdate"
						@click="addCisCookie"
					>
						Add
					</PrimaryActionButton>
				</v-col>
			</v-row>
			<v-row dense>
				<v-col cols="12">
					<DataTable
						:items="cookieBanner.cisCookies"
						:headers="cisCookieHeaders"
					>
						<template #item.cookieScriptName="{ item }">
							{{ cookieScriptName(item.cookieScriptId) }}
						</template>
						<template #item.cookieScriptType="{ item }">
							{{ cookieScriptType(item.cookieScriptId) }}
						</template>
						<template #item.action="{ index }">
							<v-menu offset-y>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										v-if="userCanCreateUpdate"
										@click="removeCisCookie(index)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Remove
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
				</v-col>
			</v-row>
		</template>
	</SectionCard>
</template>
<script>
import axios from 'axios'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../../shared/components/text-field.vue'
import Toggle from '../../../../../../../shared/components/toggle.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'

import DataTable from '../../../../../../../shared/components/data-table.vue'
import { getEnv } from '../../../../../../../shared/utils/runtime-settings.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'

export default {
	components: {
		SectionCard,
		TextField,
		Toggle,
		Dropdown,
		PrimaryActionButton,
		DataTable
	},
	props: {
		cookieBanner: Object,
		cookieBannerScripts: Array,
		updateBanner: Function,
		cdcCookieCollectionSelected: Boolean,
		allowedDomains: [Array, String],
		userCanCreateUpdate: Boolean
	},
	setup () {
		return {
			showSnackbar
		}
	},
	data () {
		return {
			selectedCookieScript: null,
			selectedCookieName: null
		}
	},
	computed: {
		cisEnabled () {
			if (this.cdcCookieCollectionSelected) {
				return true
			} else return this.cookieBanner.enableCis
		},
		cisEnvironment () {
			return getEnv('VUE_APP_CASSIE_IDENTITY_SERVICE_URL')
		},
		cookieScriptItems () {
			return this.cookieBannerScripts.map(({ cookieScriptName, id }) => ({
				text: cookieScriptName,
				value: id
			})).sort((a, b) => a.text?.localeCompare(b.text))
		},
		cookieNames () {
			const cookieNames = this.cookieBannerScripts.find(({ id }) => id === this.selectedCookieScript)?.namesOfCookiesInScript
			if (cookieNames) {
				const bannerCisCookies = this.cookieBanner.cisCookies.map(cisCookie => {
					return cisCookie.cookieName
				})
				return cookieNames.split(',').filter(cookieName => !bannerCisCookies.includes(cookieName)).map(cookieName => ({
					text: cookieName,
					value: cookieName
				}))
			} else return []
		},
		cisCookieHeaders () {
			return [
				{
					text: 'Service Name',
					value: 'cookieScriptName',
					width: '40%'
				},
				{
					text: 'Cookie Name in Browser',
					value: 'cookieName',
					width: '35%'
				},
				{
					text: 'Type',
					value: 'cookieScriptType',
					width: '25%'
				},
				{
					text: 'Action',
					value: 'action',
					width: '80px',
					align: 'center',
					sortable: false
				}
			]
		},
		mappedAllowedDomains () {
			return this.allowedDomains.map(domain => ({
				text: domain,
				value: domain
			}))
		},
		isAddButtonDisabled () {
			return this.selectedCookieName
				? !this.selectedCookieName.trim() && !this.selectedCookieScript
				: (!this.cookieNames || this.cookieNames.length === 0) || !this.selectedCookieScript
		}
	},
	methods: {
		async testConnection () {
			const idsUrl = this.cisEnvironment + this.cookieBanner.identityServiceEndpoint + '/health'
			const api = axios.create()
			api.get(idsUrl, { timeout: 5 * 1000 })
				.then(_ => {
					this.showSnackbar('Connection Test Success: ' + this.cisEnvironment + this.cookieBanner.identityServiceEndpoint)
				})
				.catch(_ => {
					this.showSnackbar({ text: 'Please check your CIS settings and try again.', color: 'red' })
				})
		},
		addCisCookie () {
			this.updateBanner('cisCookies', [
				...this.cookieBanner.cisCookies,
				{
					cookieScriptId: this.selectedCookieScript,
					cookieName: this.selectedCookieName
				}
			])
			this.selectedCookieScript = null
			this.selectedCookieName = null
		},
		removeCisCookie (index) {
			const cisCookies = this.cookieBanner.cisCookies
			this.$delete(cisCookies, index)
			this.updateBanner('cisCookies', [
				...cisCookies
			])
		},
		cookieScriptType (cookieScriptId) {
			return this.cookieBannerScripts.find(({ id }) => id === cookieScriptId)?.cookieScriptType
		},
		cookieScriptName (cookieScriptId) {
			return this.cookieBannerScripts.find(({ id }) => id === cookieScriptId)?.cookieScriptName
		},
		setCookieNameToFirstValue () {
			this.selectedCookieName = this.cookieNames?.sort((a, b) => a.text?.localeCompare(b.text))[0]?.value
		}
	}
}
</script>
